import * as THREE from 'three/webgpu';

/**
 * @file Spatial splitting of merged geometry. The last step before the bake.
 *
 * Merging by material gets the draw count down; chunking gets it down AGAIN at
 * runtime, because a 3x3 split means the frustum culler can throw away the ~2/3
 * of the city that is behind the camera. The plan's budget (city <= 60 draws)
 * assumes `materials x chunks` and roughly 15 surviving after culling.
 *
 * n=3 is the default for a reason: finer grids trade culling wins for a linear
 * increase in worst-case draws. Only raise it for very large, very flat levels.
 *
 * @module engine/world/Chunker
 */

/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('../types.js').MaterialKey} MaterialKey */

/**
 * @typedef {Object} GeoChunk
 * @property {MaterialKey} matKey
 * @property {THREE.BufferGeometry} geometry Non-indexed, all input attributes
 *   preserved (including `color`/`color1` if the bake already ran).
 * @property {THREE.Box3} box World bounds of this chunk.
 * @property {{x: number, z: number}} cell Grid coordinates, for debugging.
 * @property {number} tris
 */

/**
 * Splits every merged geometry into an `n x n` XZ grid by TRIANGLE CENTROID.
 * Triangles are never cut, so silhouettes are untouched and vertex counts are
 * preserved exactly — which is what makes the round trip testable.
 *
 * Every attribute on the input survives, whatever it is called, so this is safe
 * to run either before OR after the bake (before is cheaper: the bake then
 * processes smaller buffers and can stream).
 *
 * The INPUT geometries are not modified and not disposed — the caller owns them
 * and should dispose them once the chunks are built (they hold no shared
 * buffers).
 *
 * @param {Map<MaterialKey, THREE.BufferGeometry>} byMaterial Straight out of
 *   `GeoBuilder.merge()`.
 * @param {Bounds2} bounds World extent to grid over (usually `builder.bounds()`).
 * @param {number} [n=3] Grid resolution per axis.
 * @returns {GeoChunk[]} Empty cells are dropped, so the length is
 *   `<= materials * n * n`.
 */
export function chunkGeometries(byMaterial, bounds, n = 3) {
  /** @type {GeoChunk[]} */
  const out = [];
  const div = Math.max(1, n | 0);

  const spanX = bounds.maxX - bounds.minX;
  const spanZ = bounds.maxZ - bounds.minZ;
  // Degenerate bounds (a single prop, an empty level) => one chunk, no maths.
  const invX = spanX > 1e-6 ? div / spanX : 0;
  const invZ = spanZ > 1e-6 ? div / spanZ : 0;

  for (const [matKey, geo] of byMaterial) {
    if (!geo) continue;
    const pos = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('position'));
    if (!pos || pos.count === 0) continue;

    const src = geo.index !== null ? geo.toNonIndexed() : geo;
    const P = /** @type {ArrayLike<number>} */ (src.getAttribute('position').array);
    const total = src.getAttribute('position').count;
    const tris = total / 3;

    // Pass 1: bucket triangle ids.
    /** @type {Array<number[]|undefined>} */
    const cells = new Array(div * div);
    for (let t = 0; t < tris; t++) {
      const a = t * 9;
      const cx = (P[a] + P[a + 3] + P[a + 6]) / 3;
      const cz = (P[a + 2] + P[a + 5] + P[a + 8]) / 3;
      const gx = clampI(Math.floor((cx - bounds.minX) * invX), 0, div - 1);
      const gz = clampI(Math.floor((cz - bounds.minZ) * invZ), 0, div - 1);
      const i = gz * div + gx;
      (cells[i] ||= []).push(t);
    }

    // Pass 2: copy every attribute for the triangles in each non-empty cell.
    const attrs = Object.keys(src.attributes);
    for (let gz = 0; gz < div; gz++) {
      for (let gx = 0; gx < div; gx++) {
        const list = cells[gz * div + gx];
        if (!list || list.length === 0) continue;

        const chunk = new THREE.BufferGeometry();
        for (const name of attrs) {
          const a = /** @type {THREE.BufferAttribute} */ (src.getAttribute(name));
          const item = a.itemSize;
          const S = a.array;
          // ⚠ The destination takes the SOURCE's array type, not Float32.
          // `bake/sunKeys.js` ships visibility as `Uint8` + `normalized`, and
          // widening it here would both quadruple it and silently drop the
          // normalisation — 255 would arrive at the shader as 255.0, not 1.0.
          const dst = new (/** @type {any} */ (S.constructor))(list.length * 3 * item);
          let w = 0;
          for (let k = 0; k < list.length; k++) {
            const base = list[k] * 3;
            for (let v = 0; v < 3; v++) {
              const s = (base + v) * item;
              for (let c = 0; c < item; c++) dst[w++] = S[s + c];
            }
          }
          const out = new THREE.BufferAttribute(dst, item, a.normalized);
          out.setUsage(a.usage);
          chunk.setAttribute(name, out);
        }
        if (chunk.index !== null) throw new Error('chunkGeometries: produced an indexed chunk');
        chunk.computeBoundingBox();
        chunk.computeBoundingSphere();
        chunk.userData.materialKey = matKey;
        chunk.userData.chunk = { x: gx, z: gz };

        out.push({
          matKey,
          geometry: chunk,
          box: /** @type {THREE.Box3} */ (chunk.boundingBox),
          cell: { x: gx, z: gz },
          tris: list.length,
        });
      }
    }

    if (src !== geo) src.dispose();
  }

  return out;
}

/**
 * Convenience readout for the console line every level prints.
 * @param {GeoChunk[]} chunks
 * @returns {{chunks: number, tris: number, verts: number, materials: number}}
 */
export function chunkStats(chunks) {
  let tris = 0;
  const mats = new Set();
  for (const c of chunks) {
    tris += c.tris;
    mats.add(c.matKey);
  }
  return { chunks: chunks.length, tris, verts: tris * 3, materials: mats.size };
}

/**
 * @param {number} v @param {number} a @param {number} b
 * @returns {number}
 */
const clampI = (v, a, b) => (v < a ? a : v > b ? b : v);
